import { FC, useEffect, useState, useCallback } from 'react'
import { Box, Modal, ModalDialog, Typography, Button, IconButton } from '@mui/joy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import { AnalyticsEvent, track } from 'services/analytics'
import { POPOVER_Z_INDEX } from './ds2/z-index'

const CHROME_EXTENSION_URL = 'https://chromewebstore.google.com/detail/numeo-spot/mfhpdapldnbmejcanhlbbmjlojncicfj'

// Helper to check if extension is installed
const checkExtensionInstalled = (): boolean => {
    return localStorage.getItem('hasExtension') === 'true' || localStorage.getItem('numeo-extension-connected') === 'true'
}

interface ExtensionRequiredModalProps {
    open: boolean
    onExtensionDetected: () => void
    onClose?: () => void
    /** Which funnel the interaction belongs to, so factoring does not skew onboarding drop-off. */
    analyticsFlow?: 'onboarding' | 'factoring'
}

export const ExtensionRequiredModal: FC<ExtensionRequiredModalProps> = ({ open, onExtensionDetected, onClose, analyticsFlow = 'onboarding' }) => {
    const [isChecking, setIsChecking] = useState(false)

    // Listen for extension installation messages
    useEffect(() => {
        if (!open) return

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return

            if (event.data?.type === 'NUMEO_EXTENSION_INSTALLED' && event.data?.source === 'numeo-extension') {
                localStorage.setItem('numeo-extension-connected', 'true')
                onExtensionDetected()
            }
        }

        window.addEventListener('message', handleMessage)
        return () => window.removeEventListener('message', handleMessage)
    }, [open, onExtensionDetected])

    // Periodically check for extension
    useEffect(() => {
        if (!open) return
        track(AnalyticsEvent.EXTENSION_REQUIRED, { flow: analyticsFlow, step: 'extension_required' })

        const interval = setInterval(() => {
            if (checkExtensionInstalled()) {
                onExtensionDetected()
            }
        }, 2000)

        return () => clearInterval(interval)
    }, [analyticsFlow, open, onExtensionDetected])

    const handleCheckAgain = useCallback(() => {
        track(AnalyticsEvent.ACTION, { label: 'check_again', type: 'click', flow: analyticsFlow, step: 'extension_required' })
        setIsChecking(true)

        // Check after a brief delay
        setTimeout(() => {
            if (checkExtensionInstalled()) {
                onExtensionDetected()
            }
            setIsChecking(false)
        }, 1000)
    }, [analyticsFlow, onExtensionDetected])

    const handleInstallExtension = useCallback(() => {
        track(AnalyticsEvent.ACTION, {
            label: 'install_extension',
            type: 'click',
            flow: analyticsFlow,
            step: 'extension_required',
        })
        window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer')
    }, [analyticsFlow])

    const handleClose = useCallback(() => {
        track(AnalyticsEvent.ACTION, { label: 'close', type: 'click', flow: analyticsFlow, step: 'extension_required' })
        onClose?.()
    }, [analyticsFlow, onClose])

    if (!open) return null

    return (
        <Modal data-testid="extension-required-modal" open={open} onClose={handleClose} sx={{ zIndex: POPOVER_Z_INDEX }}>
            <ModalDialog
                variant="plain"
                sx={{
                    p: 4,
                    maxWidth: 480,
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    position: 'relative',
                }}
            >
                {/* Close button */}
                {onClose && (
                    <IconButton
                        variant="plain"
                        color="neutral"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}

                {/* Chrome Web Store Icon */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '16px',
                            bgcolor: '#EBF4FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src="https://fonts.gstatic.com/s/i/productlogos/chrome_store/v7/192px.svg"
                            alt="Chrome Web Store"
                            style={{ width: 48, height: 48 }}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                        />
                    </Box>
                </Box>

                {/* Title */}
                <Typography
                    level="h3"
                    sx={{
                        textAlign: 'center',
                        fontWeight: 600,
                        color: '#111827',
                        mb: 1.5,
                    }}
                >
                    Extension Required
                </Typography>

                {/* Description */}
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: '#6B7280',
                        fontSize: '15px',
                        lineHeight: 1.6,
                        mb: 4,
                    }}
                >
                    To use Numeo Spot, you need to install our Chrome extension. The extension enables load search, email integration, and AI-powered dispatch
                    features.
                </Typography>

                {/* Install Button */}
                <Button
                    variant="solid"
                    size="lg"
                    fullWidth
                    onClick={handleInstallExtension}
                    endDecorator={<OpenInNewIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        bgcolor: '#0067D5',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '15px',
                        borderRadius: '10px',
                        py: 1.5,
                        mb: 2,
                        '&:hover': {
                            bgcolor: '#0052CC',
                        },
                    }}
                >
                    Install Extension
                </Button>

                {/* Check Again Button */}
                <Button
                    variant="outlined"
                    size="lg"
                    fullWidth
                    onClick={handleCheckAgain}
                    loading={isChecking}
                    startDecorator={!isChecking && <RefreshIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        color: '#374151',
                        borderColor: '#D1D5DB',
                        fontWeight: 500,
                        fontSize: '14px',
                        borderRadius: '10px',
                        py: 1.25,
                        '&:hover': {
                            bgcolor: '#F9FAFB',
                            borderColor: '#9CA3AF',
                        },
                    }}
                >
                    {isChecking ? 'Checking...' : "I've installed it - Check Again"}
                </Button>

                {/* Help text */}
                <Typography
                    sx={{
                        textAlign: 'center',
                        color: '#9CA3AF',
                        fontSize: '13px',
                        mt: 3,
                    }}
                >
                    After installing, refresh this page or click "Check Again"
                </Typography>
            </ModalDialog>
        </Modal>
    )
}

export default ExtensionRequiredModal
